import { useState, useRef, useEffect, useCallback } from "react";
import { IMessage } from "../../types";
import { chatService, IMessageStream } from "../../../../services";

interface MessageSendProps {
  onMessageComplete: (message: IMessage) => void;
}

export function useSendMessage({ onMessageComplete }: MessageSendProps) {
  // Estado único para renderização
  const [response, setResponse] = useState<IMessage>({
    role: "assistant",
    date_time: new Date().toISOString(),
    content: "|",
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs para ciclo de vida e acúmulo
  const contentRef = useRef<string>("");
  const referencesRef = useRef<string[] | undefined>(undefined);
  const mountedRef = useRef(true);

  const reset = useCallback(() => {
    contentRef.current = "";
    referencesRef.current = undefined;
    setResponse({
      role: "assistant",
      date_time: new Date().toISOString(),
      content: "|",
    });
    setError(null);
  }, []);

  const sendMessageStream = useCallback(
    async (projectId: number, sessionId: number, currentMessage: string, currentFile?: File) => {
      if (isStreaming) return;
      setIsStreaming(true);
      reset();

      try {
        await chatService.sendMessageStream({
          prompt: currentMessage,
          file: currentFile,
          projectId,
          sessionId,
          callback(data: IMessageStream) {
            if (data.references) referencesRef.current = data.references;
            if (typeof data.delta === "string") contentRef.current += data.delta;

            // Atualiza apenas o estado necessário para renderização
            setResponse({
              role: "assistant",
              date_time: new Date().toISOString(),
              content: data.finish_reason ? contentRef.current : contentRef.current + "|",
              references: referencesRef.current,
            });

            if (data.finish_reason) {
              setIsStreaming(false);
              onMessageComplete({
                role: "assistant",
                date_time: new Date().toISOString(),
                content: contentRef.current,
                references: referencesRef.current,
              });
            }
          },
        });
      } catch {
        if (mountedRef.current) {
          setIsStreaming(false);
          setError("Erro ao enviar mensagem.");
        }
      }
    },
    [isStreaming, onMessageComplete, reset],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      contentRef.current = "";
      referencesRef.current = undefined;
    };
  }, []);

  return { sendMessageStream, response, isStreaming, error, reset };
}
